// src/App.jsx
// git commit: "feat(App): remove BuyNumber (all-countries smsbower page)"
import { Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FloatingSupportButton from "./components/FloatingSupportButton";
import SuggestionNudge from "./components/SuggestionNudge";
import FundingNotifier from "./components/FundingNotifier";
import { refreshIfNearExpiry } from "./api/api";
import { useEffect } from "react";
import { WalletProvider } from "./context/WalletContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ActivationsProvider } from "./context/ActivationsContext";
import { CartProvider } from "./context/CartContext";
import Cart from "./pages/Cart";

/* ===== AUTH GUARD ===== */
import ProtectedRoute from "./components/ProtectedRoute";

/* ===== USER PAGES ===== */
import Landing       from "./pages/Landing";
import Login         from "./pages/Login";
import Signup        from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CheckEmail from "./pages/CheckEmail";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard     from "./pages/Dashboard";
import Messages      from "./pages/Messages";
import Transactions  from "./pages/Transactions";
import FundWallet    from "./pages/FundWallet";
import Transfer      from "./pages/Transfer";
import BuyNumbers    from "./pages/BuyNumbers";
import Profile       from "./pages/Profile";
import SocialLogs    from "./pages/SocialLogs";
import MyOrders      from "./pages/MyOrders";
import Tutorials from "./pages/Tutorials";
import Gifts from "./pages/Gifts";
import GiftCategoryView from "./pages/GiftCategoryView";
import GiftCheckout from "./pages/GiftCheckout";
import GiftOrders from "./pages/GiftOrders";
import CookiePolicy from "./pages/CookiePolicy";
   import Referrals from "./pages/Referrals";
   import GiftCards from "./pages/GiftCards";

/* ===== DASHBOARD LAYOUT ===== */
import DashboardLayout from "./layouts/DashboardLayout";

/* ===== ADMIN PAGES ===== */
import AdminLogin        from "./pages/admin/AdminLogin";
import AdminVerify       from "./pages/admin/AdminVerify";
import AdminLayout       from "./pages/admin/AdminLayout";
import AdminDashboard    from "./pages/admin/AdminDashboard";
import Users             from "./pages/admin/Users";
import Numbers           from "./pages/admin/Numbers";
import Rentals           from "./pages/admin/Rentals";
import Wallets           from "./pages/admin/Wallets";
import AdminTransactions from "./pages/admin/Transactions";
import AdminActivations  from "./pages/admin/AdminActivations";
import AdminSocialLogs   from "./pages/admin/AdminSocialLogs";
import AdminSettings     from "./pages/admin/AdminSettings";
import AdminGifts        from "./pages/admin/AdminGifts";
import AdminBroadcast    from "./pages/admin/AdminBroadcast";
   import AdminLoginTutorials from "./pages/admin/AdminLoginTutorials";
   import AdminGiftCards from "./pages/admin/AdminGiftCards";
   import AdminManualFunding from "./pages/admin/AdminManualFunding";

/* ===== ADMIN PROTECTED ROUTE ===== */
import AdminRoute from "./pages/admin/AdminRoute";

function App() {
  useEffect(() => {
    refreshIfNearExpiry();
    const handleFocus = () => refreshIfNearExpiry();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  return (
    <>
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route path="/"                element={<Landing />} />
        <Route path="/login"           element={<Login />} />
        <Route path="/signup"          element={<Signup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email"     element={<CheckEmail />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/cookie-policy"   element={<CookiePolicy />} />

        {/* USER DASHBOARD */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <CurrencyProvider>
                <WalletProvider>
                  <ActivationsProvider>
                    <CartProvider>
                      <DashboardLayout />
                      <FundingNotifier />
                    </CartProvider>
                  </ActivationsProvider>
                </WalletProvider>
              </CurrencyProvider>
            </ProtectedRoute>
          }
        >
          <Route index                     element={<Dashboard />} />
          <Route path="usa-numbers"        element={<BuyNumbers />} />
          <Route path="tutorials"          element={<Tutorials />} />
          <Route path="history"            element={<Transactions />} />
          <Route path="wallet"             element={<FundWallet />} />
          <Route path="transfer"           element={<Transfer />} />
          <Route path="profile"            element={<Profile />} />
          <Route path="messages/:numberId" element={<Messages />} />
          <Route path="social-logs"        element={<SocialLogs />} />
          <Route path="orders"             element={<MyOrders />} />
          <Route path="gifts"                    element={<Gifts />} />
          <Route path="gifts/category/:category" element={<GiftCategoryView />} />
          <Route path="gifts/search"             element={<GiftCategoryView />} />
          <Route path="cart"                     element={<Cart />} />
          <Route path="gifts/checkout"     element={<GiftCheckout />} />
          <Route path="gift-orders"        element={<GiftOrders />} />
          <Route path="referrals"             element={<Referrals />} />
             <Route path="gift-cards"         element={<GiftCards />} />
        </Route>

        {/* ADMIN ROUTES */}
        <Route path="/admin/login"   element={<AdminLogin />} />
        <Route path="/admin/verify"  element={<AdminVerify />} />

        <Route element={<AdminRoute />}>
          <Route
            element={
              <CurrencyProvider>
                <AdminLayout />
              </CurrencyProvider>
            }
          >
            <Route path="/admin/dashboard"    element={<AdminDashboard />} />
            <Route path="/admin/users"        element={<Users />} />
            <Route path="/admin/numbers"      element={<Numbers />} />
            <Route path="/admin/rentals"      element={<Rentals />} />
            <Route path="/admin/wallets"      element={<Wallets />} />
            <Route path="/admin/transactions" element={<AdminTransactions />} />
            <Route path="/admin/activations"  element={<AdminActivations />} />
            <Route path="/admin/social-logs"  element={<AdminSocialLogs />} />
            <Route path="/admin/settings"     element={<AdminSettings />} />
            <Route path="/admin/gifts"        element={<AdminGifts />} />
            <Route path="/admin/broadcast"    element={<AdminBroadcast />} />
               <Route path="/admin/login-tutorials" element={<AdminLoginTutorials />} />
               <Route path="/admin/gift-cards" element={<AdminGiftCards />} />
               <Route path="/admin/manual-funding" element={<AdminManualFunding />} />
          </Route>
        </Route>
      </Routes>

      <FloatingSupportButton />
      <SuggestionNudge />

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick={false}
        pauseOnHover
        theme="colored"
      />
    </>
  );
}

export default App;